import React, { useEffect, useMemo, useState } from "react";
import InstallerLayout from "./InstallerLayout";
import {
  getCurrentInstallerId,
  getStats,
  seedDemoJobsIfEmpty,
  getJobs,
  getBidsByInstaller,
  getInstallerProfile,
} from "../../lib/data";
import { Link, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Target,
  Award,
  Clock,
  Briefcase,
  Rocket,
  Sparkles,
  TrendingUp,
  Calendar,
  CheckCircle2,
} from "lucide-react";

export default function InstallerDashboard({ user }) {
  const installerId = getCurrentInstallerId(user);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    jobsOpen: 0,
    myBids: 0,
    awarded: 0,
    pending: 0,
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentBids, setRecentBids] = useState([]);
  const profile = useMemo(
    () => getInstallerProfile(installerId),
    [installerId]
  );

  useEffect(() => {
    seedDemoJobsIfEmpty();
    setStats(getStats(installerId));
    setRecentJobs(getJobs().slice(0, 5));
    const mine = getBidsByInstaller(installerId);
    setRecentBids(mine.slice(0, 5));
  }, [installerId]);

  // Pipeline counts
  const pipeline = useMemo(() => {
    const bids = getBidsByInstaller(installerId);
    const submitted = bids.filter((b) => b.status === "submitted").length;
    const shortlisted = bids.filter((b) => b.status === "shortlisted").length;
    const awarded = bids.filter((b) => b.status === "awarded").length;
    const lost = bids.filter(
      (b) => b.status === "lost" || b.status === "withdrawn"
    ).length;
    const total = Math.max(1, bids.length);
    const pct = (n) => Math.round((n / total) * 100);
    return {
      submitted,
      shortlisted,
      awarded,
      lost,
      total,
      pctSubmitted: pct(submitted),
      pctShortlisted: pct(shortlisted),
      pctAwarded: pct(awarded),
      pctLost: pct(lost),
    };
  }, [installerId, recentBids.length]);

  // Mini performance chart (last 6 months bids)
  const perf = useMemo(() => {
    const byMonth = {};
    const bids = getBidsByInstaller(installerId);
    bids.forEach((b) => {
      const d = new Date(b.createdAt || b.updatedAt || Date.now());
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      byMonth[key] = (byMonth[key] || 0) + 1;
    });
    // build last 6 months labels
    const labels = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      labels.push(key);
    }
    const values = labels.map((k) => byMonth[k] || 0);
    const max = Math.max(1, ...values);
    return { labels, values, max };
  }, [installerId, recentBids.length]);

  const StatCard = ({ icon: Icon, label, value, gradient, testid }) => (
    <div
      className={`relative overflow-hidden rounded-2xl p-5 shadow-sm border border-gray-100 bg-white hover:shadow-md transition-all`}
      data-testid={testid}
    >
      <div
        className={`absolute -top-10 -right-10 w-32 h-32 ${gradient} rounded-full blur-2xl opacity-20`}
      ></div>
      <div className="relative flex items-center gap-4">
        <div
          className={`p-3 rounded-xl text-white ${
            gradient.replace("from-", "bg-").split(" ")[0] || "bg-blue-600"
          }`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-sm text-gray-500">{label}</div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
        </div>
      </div>
    </div>
  );

  return (
    <InstallerLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white p-6 md:p-8">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_20%,#ffffff33_0,transparent_40%),radial-gradient(circle_at_80%_0,#ffffff22_0,transparent_40%)]"></div>
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-blue-100 text-xs font-semibold uppercase tracking-wider">
              <Rocket className="w-4 h-4" /> Growth Dashboard
            </div>
            <h1
              className="mt-2 text-2xl md:text-3xl font-bold"
              data-testid="installer-hero-title"
            >
              Welcome back, {profile.contactName || "Installer"}
            </h1>
            <p className="mt-2 text-blue-100 max-w-xl">
              Manage your bids, track performance, and win more jobs with a
              streamlined workflow.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                data-testid="cta-browse-jobs"
                onClick={() => navigate("/installer/jobs")}
                className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/20 font-semibold flex items-center gap-2"
              >
                <Briefcase className="w-4 h-4" /> Browse Jobs
              </button>
              <button
                data-testid="cta-my-bids"
                onClick={() => navigate("/installer/bids")}
                className="px-5 py-2.5 rounded-xl bg-white text-blue-700 font-semibold hover:shadow"
              >
                View My Bids
              </button>
              <button
                data-testid="cta-edit-profile"
                onClick={() => navigate("/installer/profile")}
                className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/20 font-semibold"
              >
                Edit Profile
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 w-full lg:w-auto">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <div className="text-xs text-blue-100">Open Jobs</div>
              <div className="text-2xl font-bold" data-testid="hero-open-jobs">
                {stats.jobsOpen}
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <div className="text-xs text-blue-100">My Bids</div>
              <div className="text-2xl font-bold" data-testid="hero-my-bids">
                {stats.myBids}
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <div className="text-xs text-blue-100">Awarded</div>
              <div className="text-2xl font-bold" data-testid="hero-awarded">
                {stats.awarded}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <StatCard
          icon={BarChart3}
          label="Open Jobs"
          value={stats.jobsOpen}
          gradient="from-blue-600 to-indigo-600"
          testid="stat-open-jobs"
        />
        <StatCard
          icon={Target}
          label="My Bids"
          value={stats.myBids}
          gradient="from-indigo-600 to-violet-600"
          testid="stat-my-bids"
        />
        <StatCard
          icon={Clock}
          label="Pending"
          value={stats.pending}
          gradient="from-amber-600 to-orange-600"
          testid="stat-pending"
        />
        <StatCard
          icon={Award}
          label="Awarded"
          value={stats.awarded}
          gradient="from-emerald-600 to-teal-600"
          testid="stat-awarded"
        />
      </div>

      {/* Pipeline + Performance */}
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold text-gray-900">Pipeline Overview</div>
            <div className="text-sm text-gray-500">{pipeline.total} total</div>
          </div>
          <div className="space-y-4">
            <div
              className="flex items-center justify-between gap-4"
              data-testid="pipeline-submitted"
            >
              <div className="text-sm text-gray-700">Submitted</div>
              <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-blue-600"
                  style={{ width: `${pipeline.pctSubmitted}%` }}
                ></div>
              </div>
              <div className="text-sm font-semibold text-gray-900 w-12 text-right">
                {pipeline.pctSubmitted}%
              </div>
            </div>
            <div
              className="flex items-center justify-between gap-4"
              data-testid="pipeline-shortlisted"
            >
              <div className="text-sm text-gray-700">Shortlisted</div>
              <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-indigo-600"
                  style={{ width: `${pipeline.pctShortlisted}%` }}
                ></div>
              </div>
              <div className="text-sm font-semibold text-gray-900 w-12 text-right">
                {pipeline.pctShortlisted}%
              </div>
            </div>
            <div
              className="flex items-center justify-between gap-4"
              data-testid="pipeline-awarded"
            >
              <div className="text-sm text-gray-700">Awarded</div>
              <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-emerald-600"
                  style={{ width: `${pipeline.pctAwarded}%` }}
                ></div>
              </div>
              <div className="text-sm font-semibold text-gray-900 w-12 text-right">
                {pipeline.pctAwarded}%
              </div>
            </div>
            <div
              className="flex items-center justify-between gap-4"
              data-testid="pipeline-lost"
            >
              <div className="text-sm text-gray-700">Lost/Withdrawn</div>
              <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-rose-600"
                  style={{ width: `${pipeline.pctLost}%` }}
                ></div>
              </div>
              <div className="text-sm font-semibold text-gray-900 w-12 text-right">
                {pipeline.pctLost}%
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold text-gray-900">
              Performance (last 6 months)
            </div>
          </div>
          <div
            className="flex items-end gap-3 h-40"
            data-testid="performance-chart"
          >
            {perf.values.map((v, idx) => {
              const h = Math.round((v / perf.max) * 100);
              const label = perf.labels[idx];
              return (
                <div key={label} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-gradient-to-t from-blue-600 to-indigo-600 rounded-t-lg"
                    style={{ height: `${Math.max(6, h)}%` }}
                  ></div>
                  <div className="mt-2 text-[10px] text-gray-500">
                    {label.slice(5)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
            <TrendingUp className="w-3 h-3" /> Bids per month
          </div>
        </div>
      </div>

      {/* Lists */}
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold text-gray-900">Recent Jobs</div>
            <Link
              to="/installer/jobs"
              className="text-blue-600 text-sm"
              data-testid="dashboard-view-all-jobs"
            >
              View all
            </Link>
          </div>
          <div className="divide-y">
            {recentJobs.map((j) => (
              <Link
                to={`/installer/jobs/${j.id}`}
                key={j.id}
                className="py-3 flex items-center justify-between hover:bg-gray-50 rounded"
              >
                <div>
                  <div className="font-medium text-gray-900">{j.title}</div>
                  <div className="text-sm text-gray-500">{j.address}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">
                    {j.squareFeet.toLocaleString()} sq ft
                  </div>
                  <div className="text-xs text-gray-500">
                    {j.roofType} • {j.confidence}
                  </div>
                </div>
              </Link>
            ))}
            {recentJobs.length === 0 && (
              <div className="text-sm text-gray-500">No jobs yet</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold text-gray-900">Recent Bids</div>
            <Link
              to="/installer/bids"
              className="text-blue-600 text-sm"
              data-testid="dashboard-view-all-bids"
            >
              View all
            </Link>
          </div>
          <div className="divide-y">
            {recentBids.map((b) => (
              <div
                key={b.id}
                className="py-3 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium text-gray-900">
                    ${b.amountTotal.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">
                    {b.timelineDays} days • {b.warrantyYears} yr warranty
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      b.status === "submitted"
                        ? "bg-blue-50 text-blue-700"
                        : b.status === "shortlisted"
                        ? "bg-indigo-50 text-indigo-700"
                        : b.status === "awarded"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-gray-50 text-gray-600"
                    }`}
                  >
                    {b.status || "submitted"}
                  </span>
                  <Link
                    to={`/installer/jobs/${b.jobId}`}
                    className="text-blue-600 text-sm"
                    data-testid={`recent-bid-${b.id}`}
                  >
                    Open
                  </Link>
                </div>
              </div>
            ))}
            {recentBids.length === 0 && (
              <div className="text-sm text-gray-500">No bids yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Tips / Checklist */}
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" /> Quick Wins
          </div>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-center gap-2" data-testid="tip-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Complete
              your company profile
            </li>
            <li className="flex items-center gap-2" data-testid="tip-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Submit at
              least 3 bids this week
            </li>
            <li className="flex items-center gap-2" data-testid="tip-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Aim for
              responses within 2 hours
            </li>
          </ul>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-500" /> This Week
          </div>
          <div className="text-sm text-gray-700">
            Focus on open jobs with high confidence and larger square footage
            for best ROI.
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" /> Pro Tip
          </div>
          <div className="text-sm text-gray-700">
            Highlight warranty and timeline in your bids—homeowners weigh them
            alongside price.
          </div>
        </div>
      </div>
    </InstallerLayout>
  );
}
